import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import './FileImporter.css';

function FileImporter({ onDataImported }) {
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);

  // URLs pour les liens de téléchargement
  const downloadUrls = {
    stock: 'https://sellercentral.amazon.fr/listing/reports/ref=xx_invreport_dnav_xx',
    sales: 'https://members.helium10.com/profits/inventory-levels?accountId=1544845728&sellerName=A26O350IG3WCOL&marketplace=A13V1IB3VIYZZH'
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setImporting(true);

    // Déterminer le type de fichier
    const fileType = file.name.toLowerCase();
    const isExcel = fileType.endsWith('.xlsx') || fileType.endsWith('.xls');
    const isCSV = fileType.endsWith('.csv');
    const isTXT = fileType.endsWith('.txt');

    if (isExcel) {
      // Traitement fichier Excel (pour les moyennes de vente)
      handleExcelFile(file);
    } else if (isCSV || isTXT) {
      // Traitement fichier CSV ou TXT (pour les stocks)
      handleTextFile(file);
    } else {
      alert('Format de fichier non supporté. Veuillez importer un fichier Excel, CSV ou TXT.');
      setImporting(false);
    }
  };

  const handleExcelFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Récupérer la première feuille de calcul
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convertir en JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('Données Excel importées:', jsonData.slice(0, 2));
        
        // Détecter le type de fichier
        let fileType = 'sales-data';
        
        // Passer les données au composant parent
        if (onDataImported) {
          onDataImported(jsonData, file.name, fileType);
        }
      } catch (error) {
        console.error('Erreur lors de la lecture du fichier Excel:', error);
        alert('Erreur: Le fichier ne semble pas être un fichier Excel valide.');
      } finally {
        setImporting(false);
      }
    };
    
    reader.onerror = () => {
      console.error('Erreur lors de la lecture du fichier');
      setImporting(false);
      alert('Erreur lors de la lecture du fichier.');
    };
    
    reader.readAsArrayBuffer(file);
  };

  const handleTextFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const fileContent = e.target.result;
      const fileName = file.name.toLowerCase();
      const isCSV = fileName.endsWith('.csv');
      
      // Pour les fichiers CSV, supprimer les 7 premières lignes
      let cleanedContent = fileContent;
      if (isCSV) {
        const lines = fileContent.split('\n');
        if (lines.length <= 7) {
          console.error('Le fichier CSV contient trop peu de lignes.');
          alert('Le fichier CSV ne contient pas suffisamment de données.');
          setImporting(false);
          return;
        }
        cleanedContent = lines.slice(7).join('\n');
      }
      
      // Traiter comme un CSV ou TXT (délimité par des tabulations)
      Papa.parse(cleanedContent, {
        header: true,
        skipEmptyLines: true,
        delimiter: isCSV ? ',' : '\t',
        complete: (results) => {
          console.log('Données importées :', results.data.slice(0, 2));
          
          // Déterminer le type de fichier par son nom
          let fileType = 'unknown';
          
          // Détecter le fichier FBA (Stock traité par Amazon)
          if (fileName.includes('traité') && fileName.includes('amazon')) {
            fileType = 'stock-fba';
          } 
          // Détecter le fichier FBM (Rapport sur les listings actifs)
          else if (fileName.includes('listings') || fileName.includes('rapport')) {
            fileType = 'stock-fbm-listings';
          } 
          // Fallback pour d'autres fichiers de stock
          else if (fileName.includes('stock')) {
            fileType = 'stock-fbm';
          }
          
          console.log('Type de fichier détecté:', fileType);
          
          if (onDataImported) {
            onDataImported(results.data, file.name, fileType);
          }
          setImporting(false);
        },
        error: (error) => {
          console.error('Erreur lors de l\'analyse du fichier :', error);
          setImporting(false);
        }
      });
    };
    
    reader.readAsText(file);
  };

  // Fonction pour ouvrir un lien dans un nouvel onglet
  const openDownloadLink = (url, e) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="file-importer">
      <h2>Importer vos données</h2>
      <div className="import-container">
        <input
          type="file"
          id="file-input"
          accept=".csv,.txt,.xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <label htmlFor="file-input" className="file-button">
          Sélectionner un fichier
        </label>
        {fileName && <span className="file-name">{fileName}</span>}
      </div>
      {importing && <p>Importation en cours...</p>}
      <div className="file-instructions">
        <h3>Instructions d'importation</h3>
        <ul>
          <li>
            <strong>Fichier Excel (.xlsx):</strong> Importez votre rapport contenant les moyennes de vente sur 7 jours 
            (<a 
              href={downloadUrls.sales}
              onClick={(e) => openDownloadLink(downloadUrls.sales, e)}
              className="download-link"
              rel="noopener noreferrer"
            >
              télécharger ici
            </a>)
          </li>
          <li>
            <strong>Fichier de stock FBA (.txt):</strong> Importez le rapport "Stock traité par Amazon" 
            (<a 
              href={downloadUrls.stock}
              onClick={(e) => openDownloadLink(downloadUrls.stock, e)}
              className="download-link"
              rel="noopener noreferrer"
            >
              télécharger ici
            </a>)
          </li>
          <li>
            <strong>Fichier de stock FBM (.txt):</strong> Importez le rapport "Rapport sur les listings actifs" 
            (<a 
              href={downloadUrls.stock}
              onClick={(e) => openDownloadLink(downloadUrls.stock, e)}
              className="download-link"
              rel="noopener noreferrer"
            >
              télécharger ici
            </a>)
          </li>
        </ul>
        <p className="help-text">
          Note : Pour les fichiers CSV, les 7 premières lignes sont automatiquement ignorées.
        </p>
      </div>
    </div>
  );
}

export default FileImporter;